import { useState, useEffect } from 'react'
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion'

const BLINK_DURATION = 1.8
const BLINK_TRANSITION = {
  duration: BLINK_DURATION,
  repeat: Infinity,
  ease: 'easeInOut',
  times: [0, 0.38, 0.5, 0.62, 1],
  delay: -(Date.now() % (BLINK_DURATION * 1000)) / 1000,
}

const DOTS = (() => {
  // First row (~30 dots): 1 yellow at pos 8, 4 red at pos 12,16,20,24 rest green
  const first = Array(30).fill('#10B981')
  first[8] = '#FBBF24'
  first[12] = '#EB1700'
  first[16] = '#EB1700'
  first[20] = '#EB1700'
  first[24] = '#EB1700'
  return [...first, ...Array(363).fill('#10B981')]
})()

const accounts = [
  {
    name: 'Mercy General Hospital',
    contact: 'Sara Chen',
    role: 'Supply Chain Manager',
    label: 'Demand Sensing',
    activity: '4 SKUs flagged for Tuesday OR schedule',
    status: 'critical',
  },
  {
    name: "St. Luke's Medical Center",
    contact: 'James Park',
    role: 'Procurement Director',
    label: 'PAR Review',
    activity: 'Surgical volume up 12% — par adjustment pending',
    status: 'warning',
  },
  {
    name: 'Summit Regional Medical',
    contact: 'Lisa Torres',
    role: 'OR Coordinator',
    label: 'Inventory Sync',
    activity: 'All items in range. Next review Aug 10.',
    status: 'ok',
  },
  {
    name: 'Valley Health Partners',
    contact: 'David Kim',
    role: 'Supply Chain Lead',
    label: 'Order Status',
    activity: 'Order confirmed. Delivery ETA Aug 5.',
    status: 'ok',
  },
]

const ghostBtns = ['All Accounts', 'Escalations', 'OR Schedule', 'Reports']

const ORBIT_PROVIDERS = []

export default function EmilioDashboard({ onLogout }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        width: '100vw', height: '100vh',
        background: '#242424',
        display: 'flex', flexDirection: 'column',
        padding: 9, gap: 8, overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <TopNav onLogout={onLogout} />
      <EscalationCard />
      <AgentsCard />
    </motion.div>
  )
}

/* ── Top Nav ── */
function TopNav({ onLogout }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      style={{
        width: '100%', height: 60, flexShrink: 0,
        background: 'rgba(0,0,0,0.5)', borderRadius: 4,
        padding: '0 16px', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <motion.div
          animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ADE80', flexShrink: 0 }}
        />
        <span style={{ fontSize: 10, fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Global MedTech Supply Chain
        </span>
        <motion.button
          whileHover={{ scale: 1.12, background: 'rgba(255,255,255,0.18)' }}
          whileTap={{ scale: 0.92 }}
          onClick={onLogout}
          style={{
            width: 20, height: 20, borderRadius: 6,
            background: 'transparent', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <LogoutIcon />
        </motion.button>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        {[<SearchIcon />, <GlobeIcon />, <BoltIcon />].map((icon, i) => (
          <motion.button
            key={i}
            whileHover={{ scale: 1.12, background: 'rgba(255,255,255,0.18)' }}
            whileTap={{ scale: 0.92 }}
            transition={{ duration: 0.15 }}
            style={{
              width: 28, height: 28, borderRadius: 8,
              background: i === 2 ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.3)',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {icon}
          </motion.button>
        ))}
      </div>
    </motion.div>
  )
}

/* ── Escalation Card ── */
function EscalationCard() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.15 }}
      style={{
        width: '100%', flexShrink: 0,
        background: 'rgba(0,0,0,0.3)', borderRadius: 4,
        padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={labelStyle}>Agent Escalation Status</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {[
            { color: '#10B981', label: '300 resolved' },
            { color: '#FBBF24', label: '1 needs attention' },
            { color: '#EB1700', label: '3 critical' },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
      <DotMatrix />
    </motion.div>
  )
}

function DotMatrix() {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 3,
      overflow: 'hidden', maxHeight: 19,
    }}>
      {DOTS.map((color, i) => {
        const blink = color === '#EB1700' || color === '#FBBF24'
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={blink
              ? { opacity: [1, 1, 0, 1, 1], scale: 1 }
              : { opacity: 1, scale: 1 }}
            transition={blink
              ? { opacity: BLINK_TRANSITION, scale: { duration: 0.3, delay: 0.3 + i * 0.001, ease: 'easeOut' } }
              : { duration: 0.3, delay: 0.3 + i * 0.001, ease: 'easeOut' }}
            style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }}
          />
        )
      })}
    </div>
  )
}

/* ── Agents Card ── */
function AgentsCard() {
  const [showMercyPanel, setShowMercyPanel] = useState(false)

  return (
    <div style={{ display: 'flex', gap: 10, width: '100%', flex: 1, minHeight: 0 }}>
      {/* Orbit card */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.25 }}
        style={{
          flex: 1, minWidth: 0, minHeight: 0,
          background: 'rgba(0,0,0,0.3)', borderRadius: 4,
          padding: '16px', display: 'flex', flexDirection: 'column', gap: 24,
          position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Grid background */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, black 10%, transparent 80%)',
          maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, black 10%, transparent 80%)',
          pointerEvents: 'none',
        }} />

        {/* Background glow */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 80% 60% at 50% 100%, rgba(25,25,25,0.9) 0%, transparent 100%)',
          pointerEvents: 'none',
        }} />

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, position: 'relative', zIndex: 1 }}>
          <span style={labelStyle}>Strategic Account Agents</span>
        </div>

        {/* Orbit area */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            style={{ position: 'absolute', inset: 0 }}
          >
            <OrbitVisualization
              onSelectMercy={() => setShowMercyPanel(true)}
              mercySelected={showMercyPanel}
            />
          </motion.div>

          <div style={{
            position: 'absolute', left: 0, top: 8,
            display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start',
            pointerEvents: 'none',
          }}>
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
              style={{ fontSize: 16, fontWeight: 400, color: '#A39992', margin: 0, textAlign: 'left' }}
            >
              Good morning, Emilio
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.65 }}
              style={{ fontSize: 36, fontWeight: 500, color: '#FFFFFF', lineHeight: 1.15, margin: 0, textAlign: 'left' }}
            >
              You have 4 accounts<br />with overnight activity
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Side panel — sibling at same height */}
      <AnimatePresence>
        {showMercyPanel && <MercyPanel onClose={() => setShowMercyPanel(false)} />}
      </AnimatePresence>
    </div>
  )
}

const GREEN_DOT_NAMES = {
  1: 'Northwest Medical',
  2: 'Cedar Springs Hospital',
  3: 'Harbor View Medical',
  4: 'Eastside Health',
  6: 'Pine Valley Hospital',
  8: 'Lakewood Medical',
  10: 'Sunrise Health',
  11: 'Westbrook Hospital',
}

function OrbitVisualization({ onSelectMercy, mercySelected }) {
  const cx = 450, cy = 280, r = 170, DOT_COUNT = 12
  const orbitColor = s => s === 'critical' ? '#EB1700' : s === 'warning' ? '#FBBF24' : '#10B981'
  const [hoveredDot, setHoveredDot] = useState(null)

  return (
    <svg viewBox="0 0 900 560" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fill="#A39992" fontSize="13" fontFamily="system-ui, sans-serif">
        Tuesday, August 3, 2026
      </text>
      {Array.from({ length: DOT_COUNT }, (_, i) => {
        const a = (i * 360 / DOT_COUNT) * Math.PI / 180
        const dotX = cx + r * Math.cos(a)
        const dotY = cy + r * Math.sin(a)

        if (i === 0) {
          const h = hoveredDot === 0
          return (
            <g key={i} onMouseEnter={() => setHoveredDot(0)} onMouseLeave={() => setHoveredDot(null)} onClick={onSelectMercy} style={{ cursor: 'pointer' }}>
              <rect x={dotX + 29} y={dotY - 11} width={155} height={22} rx={5}
                fill={mercySelected ? 'white' : 'rgba(255,255,255,0.05)'}
                stroke={mercySelected ? 'white' : 'rgba(255,255,255,0.08)'} strokeWidth="1" />
              <motion.circle cx={dotX + 38} cy={dotY} r={3} fill="#EB1700"
                animate={mercySelected || h ? { opacity: 1 } : { opacity: [1, 1, 0, 1, 1] }}
                transition={mercySelected || h ? {} : BLINK_TRANSITION}
              />
              <text x={dotX + 46} y={dotY} dominantBaseline="middle"
                fill={mercySelected ? '#111111' : 'white'} fontSize="11" fontFamily="system-ui, sans-serif">
                Mercy General Hospital
              </text>
              {h && !mercySelected && (
                <motion.circle cx={dotX} cy={dotY} r={19} fill="none"
                  stroke="rgba(235,23,0,0.4)" strokeWidth={1.5}
                  animate={{ r: [19, 32], opacity: [0.4, 0] }}
                  transition={{ duration: 1.1, repeat: Infinity, ease: 'easeOut' }}
                />
              )}
              <motion.circle cx={dotX} cy={dotY}
                animate={{ r: h && !mercySelected ? 22 : 19 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                fill={mercySelected ? 'white' : (h ? 'rgba(235,23,0,0.15)' : 'rgba(255,255,255,0.05)')}
                stroke={mercySelected ? 'white' : (h ? 'rgba(235,23,0,0.35)' : 'rgba(255,255,255,0.08)')} strokeWidth="1" />
              <g transform={`translate(${dotX},${dotY})`} stroke={mercySelected || h ? '#EB1700' : 'white'} fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity={0.85}>
                <g transform="translate(-10,-10) scale(0.833)">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
                  <line x1="3.27" y1="6.96" x2="12" y2="12.01" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </g>
              </g>
            </g>
          )
        }

        if (i === 7) {
          const h = hoveredDot === 7
          return (
            <g key={i} onMouseEnter={() => setHoveredDot(7)} onMouseLeave={() => setHoveredDot(null)} style={{ cursor: 'pointer' }}>
              <rect x={dotX - 166} y={dotY - 11} width={137} height={22} rx={5}
                fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
              <motion.circle cx={dotX - 157} cy={dotY} r={3} fill="#FBBF24"
                animate={h ? { opacity: 1 } : { opacity: [1, 1, 0, 1, 1] }}
                transition={h ? {} : BLINK_TRANSITION}
              />
              <text x={dotX - 149} y={dotY} dominantBaseline="middle" fill="white" fontSize="11" fontFamily="system-ui, sans-serif">
                Valley Presbyterian
              </text>
              {h && (
                <motion.circle cx={dotX} cy={dotY} r={19} fill="none"
                  stroke="rgba(251,191,36,0.4)" strokeWidth={1.5}
                  animate={{ r: [19, 32], opacity: [0.4, 0] }}
                  transition={{ duration: 1.1, repeat: Infinity, ease: 'easeOut' }}
                />
              )}
              <motion.circle cx={dotX} cy={dotY}
                animate={{ r: h ? 22 : 19 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                fill={h ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.05)'}
                stroke={h ? 'rgba(251,191,36,0.25)' : 'rgba(255,255,255,0.08)'} strokeWidth="1" />
              <g transform={`translate(${dotX},${dotY})`} stroke={h ? '#FBBF24' : 'white'} fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity={0.85}>
                <g transform="translate(-10,-10) scale(0.833)">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </g>
              </g>
            </g>
          )
        }

        if (i === 5) {
          const h = hoveredDot === 5
          return (
            <g key={i} onMouseEnter={() => setHoveredDot(5)} onMouseLeave={() => setHoveredDot(null)} style={{ cursor: 'pointer' }}>
              <rect x={dotX - 220} y={dotY - 11} width={191} height={22} rx={5}
                fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
              <motion.circle cx={dotX - 211} cy={dotY} r={3} fill="#EB1700"
                animate={h ? { opacity: 1 } : { opacity: [1, 1, 0, 1, 1] }}
                transition={h ? {} : BLINK_TRANSITION}
              />
              <text x={dotX - 203} y={dotY} dominantBaseline="middle" fill="white" fontSize="11" fontFamily="system-ui, sans-serif">
                Riverside Community Hospital
              </text>
              {h && (
                <motion.circle cx={dotX} cy={dotY} r={19} fill="none"
                  stroke="rgba(235,23,0,0.4)" strokeWidth={1.5}
                  animate={{ r: [19, 32], opacity: [0.4, 0] }}
                  transition={{ duration: 1.1, repeat: Infinity, ease: 'easeOut' }}
                />
              )}
              <motion.circle cx={dotX} cy={dotY}
                animate={{ r: h ? 22 : 19 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                fill={h ? 'rgba(235,23,0,0.12)' : 'rgba(255,255,255,0.05)'}
                stroke={h ? 'rgba(235,23,0,0.25)' : 'rgba(255,255,255,0.08)'} strokeWidth="1" />
              <g transform={`translate(${dotX},${dotY})`} stroke={h ? '#EB1700' : 'white'} fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity={0.85}>
                <g transform="translate(-10,-10) scale(0.833)">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                </g>
              </g>
            </g>
          )
        }

        if (i === 9) {
          const h = hoveredDot === 9
          return (
            <g key={i} onMouseEnter={() => setHoveredDot(9)} onMouseLeave={() => setHoveredDot(null)} style={{ cursor: 'pointer' }}>
              <rect x={dotX - 61} y={dotY - 51} width={122} height={22} rx={5}
                fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
              <motion.circle cx={dotX - 52} cy={dotY - 40} r={3} fill="#EB1700"
                animate={h ? { opacity: 1 } : { opacity: [1, 1, 0, 1, 1] }}
                transition={h ? {} : BLINK_TRANSITION}
              />
              <text x={dotX - 44} y={dotY - 40} dominantBaseline="middle" fill="white" fontSize="11" fontFamily="system-ui, sans-serif">
                St Lukes Regional
              </text>
              <g transform={`translate(${dotX}, ${dotY})`}>
                {h && (
                  <motion.circle cx={0} cy={0} r={19} fill="none"
                    stroke="rgba(235,23,0,0.4)" strokeWidth={1.5}
                    animate={{ r: [19, 32], opacity: [0.4, 0] }}
                    transition={{ duration: 1.1, repeat: Infinity, ease: 'easeOut' }}
                  />
                )}
                <motion.circle cx={0} cy={0}
                  animate={{ r: h ? 22 : 19 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  fill={h ? 'rgba(235,23,0,0.12)' : 'rgba(255,255,255,0.05)'}
                  stroke={h ? 'rgba(235,23,0,0.25)' : 'rgba(255,255,255,0.08)'} strokeWidth="1" />
                <g transform="translate(-9,-7.5) scale(0.7)" stroke={h ? '#EB1700' : 'white'} fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.37 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  <line x1={23} y1={1} x2={16} y2={8} />
                  <line x1={16} y1={1} x2={23} y2={8} />
                </g>
              </g>
            </g>
          )
        }

        const name = GREEN_DOT_NAMES[i] || 'Hospital'
        const h = hoveredDot === i
        const onRight = dotX >= cx
        const pillW = Math.round(name.length * 6.0 + 23)
        const pillX = onRight ? dotX + 16 : dotX - 16 - pillW
        return (
          <g key={i} onMouseEnter={() => setHoveredDot(i)} onMouseLeave={() => setHoveredDot(null)} style={{ cursor: 'pointer' }}>
            <motion.circle
              cx={dotX} cy={dotY} fill="#10B981"
              animate={h ? { r: 6, opacity: 1 } : { r: 4, opacity: 0.9 }}
              transition={{ duration: 0.15 }}
            />
            {h && (
              <>
                <rect x={pillX} y={dotY - 11} width={pillW} height={22} rx={5}
                  fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                <circle cx={pillX + 9} cy={dotY} r={3} fill="#10B981" />
                <text x={pillX + 17} y={dotY} dominantBaseline="middle" fill="white" fontSize="11" fontFamily="system-ui, sans-serif">
                  {name}
                </text>
              </>
            )}
          </g>
        )
      })}
      {ORBIT_PROVIDERS.map((p, i) => {
        const a = p.angle * Math.PI / 180
        const px = cx + r * Math.cos(a), py = cy + r * Math.sin(a)
        const c = orbitColor(p.status)
        const cosA = Math.cos(a), sinA = Math.sin(a)
        let lx, ly
        if (Math.abs(cosA) >= Math.abs(sinA)) {
          lx = px + Math.sign(cosA) * 135; ly = py
        } else {
          lx = px; ly = py + Math.sign(sinA) * 51
        }
        return (
          <g key={i}>
            <rect x={px - 14} y={py - 14} width={28} height={28} rx={7}
              fill="rgba(255,255,255,0.05)" stroke={`${c}44`} strokeWidth="1" />
            <g transform={`translate(${px},${py})`} stroke={c} fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity={0.85}>
              <g transform="translate(-10,-10) scale(0.833)">
                {p.icon === 'document' && <>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </>}
                {p.icon === 'hexagon' && (
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                )}
                {p.icon === 'package' && <>
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
                  <line x1="3.27" y1="6.96" x2="12" y2="12.01" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </>}
              </g>
            </g>
            <rect x={lx - 95} y={ly - 13} width={190} height={26} rx={6}
              fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            <circle cx={lx - 78} cy={ly} r={3} fill={c} />
            <text x={lx - 69} y={ly + 4} fill="white" fontSize="11" fontFamily="system-ui, sans-serif">{p.name}</text>
          </g>
        )
      })}
    </svg>
  )
}

const CONV_MSGS = [
  { sender: "J&J DEMAND SENSING AGENT", time: "08/02/26, 11:41 PM", right: false,
    text: "Initiating nightly consumption review for Mercy General Hospital (Account #00004821). Reviewing par levels, OR schedule for the week of August 3, and 30-day consumption trends across the surgical supply portfolio. Three products are approaching threshold: ENDOPATH XCEL Dilating Tip Trocar 11mm (D11LT), ENDOPATH DEXTRUS Med Access Retractor (FLR02), and ENDOPATH BASX Bladeless Trocar 11mm (TB11LT). A fourth — ENDOPATH Clip Applier MCS20 (CK094) — is approaching threshold on a slower trajectory. Cross-referencing with Mercy General OR schedule now. Requesting par-level sync from your inventory agent." },
  { sender: "MERCY GENERAL INVENTORY AGENT", time: "08/02/26, 11:44 PM", right: true,
    text: "Sync confirmed. Current stock positions: D11LT (XCEL Trocar 11mm) — 18 units on hand, par 50. Four laparoscopic procedures scheduled Tuesday morning; projected shortfall is highest-risk item. TB11LT (BASX Trocar 11mm) — 9 units on hand, par 20. FLR02 (Dextrus Med Retractor) — 11 units on hand, par 13. Consumption on FLR02 is running 34% above the prior 30-day average; cause not identified in our system. CK094 (Clip Applier MCS20) — 14 units on hand, par 24. All four are below threshold or within 15% of par ahead of a high-volume OR week." },
  { sender: "J&J DEMAND SENSING AGENT", time: "08/02/26, 11:49 PM", right: false,
    text: "Thank you. Flagging all four for Sara's review. On the FLR02 consumption anomaly: reviewing comparable accounts with similar case mix. Three accounts showed a statistically similar 30-day spike in the 6 weeks preceding a surgical lead preference shift — in two cases linked to a new attending physician with a different retraction technique preference; in one case cause was not identified and the spike resolved within 3 weeks. Recommending Sara confirm with the OR nurse manager before adjusting par on FLR02. The Trocar shortfall (D11LT, TB11LT) is time-sensitive given Tuesday's OR schedule — recommend prioritizing order creation on those two." },
  { sender: "MERCY GENERAL INVENTORY AGENT", time: "08/02/26, 11:53 PM", right: true,
    text: "Agreed. Brief summary compiled: four SKUs, two urgent (Trocars ahead of Tuesday laparoscopic cases), one anomaly requiring clinical context (FLR02 Retractor spike), one secondary threshold (CK094 Clip Applier). Ready to surface to Sara Chen for morning review. No further sync items." },
]

function MercyPanel({ onClose }) {
  const [visibleMsgs, setVisibleMsgs] = useState(0)
  const [deployed, setDeployed] = useState(false)
  const deployControls = useAnimationControls()
  const allShown = visibleMsgs >= CONV_MSGS.length

  const handleDeploy = async () => {
    if (deployed) return
    await deployControls.start({ scale: [1, 0.92, 1.06, 1], transition: { duration: 0.38, ease: 'easeOut' } })
    setDeployed(true)
  }

  return (
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: 424 }}
      exit={{ width: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      style={{ flexShrink: 0, height: '100%', overflow: 'hidden', borderRadius: 4 }}
    >
    <div style={{
        width: 424, height: '100%',
        background: 'rgba(0,0,0,0.3)',
        padding: 16, display: 'flex', flexDirection: 'column', gap: 20,
        boxSizing: 'border-box', overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <span style={labelStyle}>Demand Sensing</span>
        <motion.button
          whileHover={{ background: 'rgba(255,255,255,0.1)' }}
          whileTap={{ scale: 0.92 }}
          onClick={onClose}
          style={{ width: 24, height: 24, borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 11 }}
        >✕</motion.button>
      </div>

      {/* Title */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
        <span style={{ fontSize: 20, fontWeight: 500, color: '#FFFFFF', letterSpacing: '-0.2px', lineHeight: 1.4 }}>Mercy General Hospital Brief</span>
        <span style={{ fontSize: 12, color: '#D5CFC9', lineHeight: 1.4 }}>00004821</span>
      </div>

      {/* Brief */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
        <span style={labelStyle}>Brief</span>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 4, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 20, fontWeight: 500, color: '#FFFFFF', letterSpacing: '-0.2px', lineHeight: 1.3 }}>4 SKUs flagged for 9/1 OR schedule</span>
            <span style={{ fontSize: 12, color: '#D5CFC9', lineHeight: 1.4 }}>J&J Demand Sensing Agent + Mercy General Inventory Agent<br />08/02/26, 11:53 PM</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF' }}>Summary</span>
            <p style={{ fontSize: 12, color: '#E8E6E3', lineHeight: 1.5, margin: 0 }}>
              Overnight analysis flagged 4 products below par threshold ahead of a high-volume OR week. Two Trocar items require priority attention before Tuesday's laparoscopic cases. One Retractor item shows a consumption anomaly that may warrant a conversation with the OR nurse manager before you adjust par. One Clip Applier is approaching threshold on a slower timeline.
            </p>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '4px 0', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF', flex: 1 }}>SKUs</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <motion.button
              animate={deployControls}
              onClick={handleDeploy}
              whileHover={deployed ? {} : { opacity: 0.85 }}
              whileTap={deployed ? {} : { scale: 0.97 }}
              style={{
                flex: 2.3, height: 26, borderRadius: 8,
                background: deployed ? '#10B981' : '#EB1700',
                border: 'none', cursor: deployed ? 'default' : 'pointer',
                color: '#FFFFFF', fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                transition: 'background 0.5s ease',
                overflow: 'hidden', position: 'relative',
              }}
            >
              <AnimatePresence mode="wait">
                {deployed ? (
                  <motion.span key="deployed"
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                  >
                    ✓ Deployed to MGH Agent
                  </motion.span>
                ) : (
                  <motion.span key="deploy"
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.15 }}
                  >
                    Deploy to MGH Agent
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
            <motion.button whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.97 }}
              style={{ flex: 1.3, height: 26, borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: 'none', cursor: 'pointer', color: '#FFFFFF', fontSize: 12, fontWeight: 500, fontFamily: 'inherit' }}>
              Escalate
            </motion.button>
          </div>
        </div>
      </div>

      {/* Agent Conversation */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={labelStyle}>Agent Conversation</span>
        <div
          onClick={() => { if (!allShown) setVisibleMsgs(v => v + 1) }}
          style={{
            background: 'rgba(0,0,0,0.2)', borderRadius: 4, padding: 20,
            display: 'flex', flexDirection: 'column', gap: 20,
            cursor: allShown ? 'default' : 'pointer', userSelect: 'none',
          }}
        >
          <AnimatePresence>
            {CONV_MSGS.slice(0, visibleMsgs).map((msg, i) => (
              <ConvMsg key={i} sender={msg.sender} time={msg.time} right={msg.right}>
                {msg.text}
              </ConvMsg>
            ))}
          </AnimatePresence>
          {!allShown && (
            <motion.div
              animate={{ opacity: [0.35, 0.65, 0.35] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textAlign: 'center', letterSpacing: '0.06em' }}
            >
              {visibleMsgs === 0 ? 'Click to view conversation' : 'Click to continue'}
            </motion.div>
          )}
        </div>
      </div>
    </div>
    </motion.div>
  )
}

function ConvMsg({ sender, time, children, right }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: right ? 'flex-end' : 'flex-start' }}
    >
      <span style={{ fontSize: 9, fontWeight: 700, color: 'rgba(248,248,247,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: right ? 'right' : 'left' }}>
        {sender} · {time}
      </span>
      <p style={{ fontSize: 12, color: '#F8F8F7', lineHeight: 1.5, margin: 0, textAlign: right ? 'right' : 'left' }}>
        {children}
      </p>
    </motion.div>
  )
}

function AccountCard({ account, index }) {
  const statusColor = account.status === 'critical' ? '#EB1700' : account.status === 'warning' ? '#FBBF24' : '#10B981'
  const [hovered, setHovered] = useState(false)

  return (
    <motion.div
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -2, transition: { duration: 0.18 } }}
      style={{
        background: 'rgba(0,0,0,0.35)', borderRadius: 6,
        padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6,
        boxShadow: hovered ? '0 0 0 1px rgba(255,255,255,0.15), 0 8px 24px rgba(0,0,0,0.3)' : '0 0 0 1px rgba(255,255,255,0.05)',
        cursor: 'default', transition: 'box-shadow 0.18s',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Status accent line */}
      <motion.div
        animate={{ scaleY: hovered ? 1 : 0, opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.18 }}
        style={{
          position: 'absolute', left: 0, top: 4, bottom: 4, width: 2,
          background: statusColor, borderRadius: 1, transformOrigin: 'center',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: '#A39992', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {account.label}
        </span>
        <motion.div
          animate={account.status === 'critical' ? { boxShadow: ['0 0 0 0px rgba(235,23,0,0)', '0 0 0 3px rgba(235,23,0,0.25)', '0 0 0 0px rgba(235,23,0,0)'] } : {}}
          transition={{ duration: 2.2, repeat: Infinity }}
          style={{
            width: 6, height: 6, borderRadius: '50%',
            background: statusColor, marginTop: 2, flexShrink: 0,
          }}
        />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF', lineHeight: 1.3 }}>{account.name}</span>
      <span style={{ fontSize: 11, color: '#A39992' }}>{account.contact} · {account.role}</span>
      <span style={{ fontSize: 11, color: '#D5CFC9', lineHeight: 1.4, fontStyle: 'italic' }}>{account.activity}</span>
    </motion.div>
  )
}

/* ── Icons ── */
function LogoutIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
      <path d="M4 2H2a1 1 0 00-1 1v5a1 1 0 001 1h2" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M7.5 3.5L10 5.5l-2.5 2" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="10" y1="5.5" x2="4.5" y2="5.5" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}
function SearchIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
      <circle cx="4.5" cy="4.5" r="3.2" stroke="rgba(255,255,255,0.7)" strokeWidth="1.3" />
      <line x1="6.9" y1="6.9" x2="9.5" y2="9.5" stroke="rgba(255,255,255,0.7)" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}
function GlobeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <circle cx="6" cy="6" r="4.5" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
      <ellipse cx="6" cy="6" rx="2" ry="4.5" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
      <line x1="1.5" y1="6" x2="10.5" y2="6" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
    </svg>
  )
}
function BoltIcon({ small }) {
  const s = small ? 8 : 10
  return (
    <svg width={s} height={s * 1.2} viewBox="0 0 10 12" fill="none">
      <path d="M6 1L1 7h4l-1 4 5-6H5l1-4z" fill="rgba(255,255,255,0.6)" />
    </svg>
  )
}
function GridIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <rect x="1" y="1" width="4" height="4" rx="1" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
      <rect x="7" y="1" width="4" height="4" rx="1" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
      <rect x="1" y="7" width="4" height="4" rx="1" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
      <rect x="7" y="7" width="4" height="4" rx="1" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
    </svg>
  )
}

const labelStyle = {
  fontSize: 10, fontWeight: 700,
  color: '#A39992', textTransform: 'uppercase', letterSpacing: '0.09em',
}
